import { gql } from "@apollo/client";

// ─── Shared field strings ──────────────────────────────────────────────────

const AMAZON_PROFILE_FIELDS = `
    mwsSelledId
    companyName
    cnktId
    linkerId
    authToken
    email
    emailValid
    createdAt
    billGlobalDay
    billGlobalDayEnabled
    noGlobalBeforeTS
    defaultClaveSatProdServ
    defaultUnidadSat
    lastGlobalBill
    sendOnShipping
    selfBillingEnabled
    billDayLimit
    billDayLimitParam
    billDayLimitEnabled
    clusterManaged
    spAccessLastGrantFail
    spConsentUrl
    excludeBusinessOrders
`;

const FISCALPOP_PROFILE_FIELDS = `
    rfc
    regimenFiscal
    nombre
    lugarExpedicion
    logoUrl
`;

const BILLING_FIELDS = `
    userId
    lastPayment
    lastPaymentId
    balance
    enabled
    isClusterType
    clusterName
    hasStripeSubscription
    stripeSubscriptionStatus
    stripeSubscriptionGracePeriodEnd
    stripeSubscriptionMaxedOut
    stripeSubscriptionCfdiUsage {
        maxUsage
        currentUsage
        isMeteredBilling
        periodStartsAt
        periodEndsAt
        priceName
        priceId
    }
`

const ORDER_FIELDS = `
    LatestShipDate
    PurchaseDate
    AmazonOrderId
    LastUpdateDate
    IsReplacementOrder
    OrderStatus
    NumberOfItemsShipped
    NumberOfItemsUnshipped
    SalesChannel
    PaymentMethod
    PaymentMethodDetails
    IsPrime
    FulfillmentChannel
    _linkSent
    OrderTotal {
        Amount
        CurrencyCode
    }
`;

const ORDER_BINDER_FIELDS = `
    orderId
    billUUID
    billIsGlobal
    refundIds
    mwsSelledId
    refundBillUUID {
        uuid
        refundIds
    }
`;

// Taxes used in query results (no "tipo")
const ITEM_BINDER_TAXES_FIELDS = `
    type
    tasa
    exento
    cuota
`;

// Taxes used in mutation results and getItemBinders (includes "tipo")
const ITEM_BINDER_TAXES_FULL_FIELDS = `
    type
    tipo
    exento
    tasa
    cuota
`;

const REPORT_TEMPLATE_FIELDS = `
    userId
    name
    description
    facturaFields
    pedidoFields
    shippingFields
`;

const REPORT_JOB_FIELDS = `
    _id
    createdAt
    working
    hasStarted
    dataUrl
    startDate
    endDate
    templateIs {
        ${REPORT_TEMPLATE_FIELDS}
    }
`;

const CONEKTA_CARD_FIELDS = `
    id
    name
    exp_month
    exp_year
    object
    type
    created_at
    last4
    brand
`;

const NEWS_WALL_CONFIRMED_FIELDS = `
    _id
    profileId
    newsId
`;

// ─── SearchOrder field strings ─────────────────────────────────────────────

// Leaf scalars
const SEARCH_ORDER_AMOUNT_FIELDS = `
    amount
    currencyCode
`;

const SEARCH_ORDER_TAX_FIELDS = `
    type
    tasa
    exento
    retencion
`;

const SEARCH_ORDER_SALES_CHANNEL_FIELDS = `
    channelName
    marketplaceId
    marketplaceName
`;

const SEARCH_ORDER_BUYER_FIELDS = `
    buyerName
    buyerEmail
    buyerCompanyName
`;

// Composed item-level types
const SEARCH_ORDER_ITEM_PRODUCT_FIELDS = `
    asin
    title
    sellerSku
    price {
        unitPrice {
            ${SEARCH_ORDER_AMOUNT_FIELDS}
        }
    }
`;

const SEARCH_ORDER_ITEM_PROCEEDS_FIELDS = `
    proceedsTotal {
        ${SEARCH_ORDER_AMOUNT_FIELDS}
    }
`;

const SEARCH_ORDER_ITEM_FIELDS = `
    orderItemId
    quantityOrdered
    product {
        ${SEARCH_ORDER_ITEM_PRODUCT_FIELDS}
    }
    proceeds {
        ${SEARCH_ORDER_ITEM_PROCEEDS_FIELDS}
    }
`;

const SEARCH_ORDER_PROCEEDS_FIELDS = `
    grandTotal {
        ${SEARCH_ORDER_AMOUNT_FIELDS}
    }
`;

// Top-level SearchOrder
const SEARCH_ORDER_FIELDS = `
    orderId
    createdTime
    lastUpdatedTime
    programs
    orderStatus
    salesChannel {
        ${SEARCH_ORDER_SALES_CHANNEL_FIELDS}
    }
    orderItems {
        ${SEARCH_ORDER_ITEM_FIELDS}
    }
    proceeds {
        ${SEARCH_ORDER_PROCEEDS_FIELDS}
    }
    buyer {
        ${SEARCH_ORDER_BUYER_FIELDS}
    }
    _linkSent
`;

// Tax group (used in global binded)
const SEARCH_ORDER_TAX_GROUP_FIELDS = `
    subtotal
    taxes {
        ${SEARCH_ORDER_TAX_FIELDS}
    }
`;

// Item binder for search orders
const SEARCH_ORDER_ITEM_BINDER_FIELDS = `
    productId
    claveProdServ
    claveUnidad
    taxes {
        ${SEARCH_ORDER_TAX_FIELDS}
    }
`;

// Composed binded types — ORDER_BINDER_FIELDS reused for AmazonOrderBinder
const SEARCH_ORDER_ITEM_BINDED_FIELDS = `
    item {
        ${SEARCH_ORDER_ITEM_FIELDS}
    }
    binder {
        ${SEARCH_ORDER_ITEM_BINDER_FIELDS}
    }
`;

const SEARCH_ORDER_BINDED_FIELDS = `
    order {
        ${SEARCH_ORDER_FIELDS}
    }
    binder {
        ${ORDER_BINDER_FIELDS}
    }
`;

const SEARCH_ORDER_GLOBAL_BINDED_FIELDS = `
    order {
        ${SEARCH_ORDER_FIELDS}
    }
    binder {
        ${ORDER_BINDER_FIELDS}
    }
    taxing {
        ${SEARCH_ORDER_TAX_GROUP_FIELDS}
    }
`;

const SEARCH_ORDER_WITH_PRODUCTS_FIELDS = `
    order {
        ${SEARCH_ORDER_BINDED_FIELDS}
    }
    products {
        ${SEARCH_ORDER_ITEM_BINDED_FIELDS}
    }
`;

const SEARCH_ORDER_PAGINATED_FIELDS = `
    orders {
        ${SEARCH_ORDER_BINDED_FIELDS}
    }
    pages
    page
`;

// ─── Schema ────────────────────────────────────────────────────────────────

export const graphqlSchema = {
    PROFILE: {
        me: gql`
        query getMe{
            me {
                amazonProfile {
                    ${AMAZON_PROFILE_FIELDS}
                }
                fiscalpopProfile {
                    ${FISCALPOP_PROFILE_FIELDS}
                }
                fiscalpopProfileStatus {
                    modulusMatch
                    cerModulus
                    cerNumber
                    notAfter
                    notBefore
                    keyModulus
                }
                billing {
                    ${BILLING_FIELDS}
                }
                notify {
                    selfBilling
                }
            }
        }`,
        login: gql`
        mutation Login($login:AccountLogin!) {
            login(login: $login){
                amazonProfile {
                    ${AMAZON_PROFILE_FIELDS}
                }
                token
            }
        }`,
        passwordReset: gql`
        mutation PasswordReset($token:String!, $newPassword: String!) {
            passwordReset(token: $token, newPassword: $newPassword){
                amazonProfile {
                    ${AMAZON_PROFILE_FIELDS}
                }
                token
            }
        }`,
        requestPasswordReset: gql`
        mutation RequestPasswordReset($email: String!){
            requestPasswordReset(email:$email)
        }`,
        validateToken: gql`
        mutation ValidateToken($token: String!){
            validateToken(token:$token)
        }`,
        acknowledgeNotification: gql`
        mutation AcknowledgeNotification($profileId: String, $property: String){
            acknowledgeNotification(profileId:$profileId, property: $property) {
                selfBilling
            }
        }`,
        getBillingAccount: gql`
        query GetBillingAccount {
            getBillingAccount {
                ${BILLING_FIELDS}
            }
        }`,
        SETUP: {
            registerAccount: gql`
            mutation RegisterAccount($account: AccountRegistration!) {
                registerAccount(account: $account){
                    amazonProfile {
                        ${AMAZON_PROFILE_FIELDS}
                    }
                    spConsentUrl
                    token
                }
            }`,
            setNameRfcChange: gql`
            mutation SetNameRfcChange($nombre: String, $rfc: String){
                setNameRfcChange(nombre: $nombre, rfc: $rfc) {
                    ${FISCALPOP_PROFILE_FIELDS}
                }
            }`,
            setLugarExpedicionChange: gql`
            mutation SetLugarExpedicionChange($lugarExpedicion: String!, $regimenFiscal: String!){
                setLugarExpedicionChange(lugarExpedicion: $lugarExpedicion, regimenFiscal: $regimenFiscal) {
                    ${FISCALPOP_PROFILE_FIELDS}
                }
            }`,
            setGlobalBillingLimits: gql`
            mutation SetGlobalBillingLimits($billGlobalDay: Float!){
                setGlobalBillingLimits(billGlobalDay: $billGlobalDay){
                    billGlobalDay
                    billDayLimit
                }
            }`,
            setGlobalBillingEnabled: gql`
            mutation SetGlobalBillingEnabled($billGlobalDayEnabled: Boolean!){
                setGlobalBillingEnabled(billGlobalDayEnabled: $billGlobalDayEnabled){
                    billGlobalDayEnabled
                }
            }`,
            setGlobalNoGlobalBefore: gql`
            mutation SetGlobalNoGlobalBefore($noGlobalBeforeTS: Float!){
                setGlobalNoGlobalBefore(noGlobalBeforeTS: $noGlobalBeforeTS){
                    billGlobalDayEnabled
                    noGlobalBeforeTS
                    billGlobalDay
                }
            }`,
            setChargeOnShipping: gql`
            mutation SetChargeOnShipping($charge: Boolean!){
                setChargeOnShipping(charge: $charge){
                    chargeShippingConcept
                }
            }`,
            setLinkTriggerOnShipping: gql`
            mutation SetLinkTriggerOnShipping($sendOnShipping: Boolean!){
                setLinkTriggerOnShipping(sendOnShipping: $sendOnShipping){
                    sendOnShipping
                }
            }`,
            setSelfBillingEnabled: gql`
            mutation SetSelfBillingEnabled($selfBillingEnabled: Boolean!){
                setSelfBillingEnabled(selfBillingEnabled: $selfBillingEnabled){
                    selfBillingEnabled
                }
            }`,
            setSelfBillingLimitsEnabled: gql`
            mutation SetSelfBillingLimitsEnabled($billDayLimitEnabled: Boolean!){
                setSelfBillingLimitsEnabled(billDayLimitEnabled: $billDayLimitEnabled){
                    billDayLimitEnabled
                }
            }`,
            setExcludeBusinessOrders: gql`
            mutation SetExcludeBusinessOrders($excludeBusinessOrders: Boolean!){
                setExcludeBusinessOrders(excludeBusinessOrders: $excludeBusinessOrders){
                    excludeBusinessOrders
                }
            }`,
            setSelfBillingLimits: gql`
            mutation SetSelfBillingLimits($limits: selfBillingLimits!){
                setSelfBillingLimits(limits: $limits){
                    billDayLimitEnabled
                    billDayLimitParam
                    billDayLimit
                }
            }`,
            setSATdefaults: gql`
            mutation SetSATdefaults($defaults: satDefaults!){
                setSATdefaults(defaults: $defaults){
                    defaultClaveSatProdServ
                    defaultUnidadSat
                }
            }
            `,
        },
        ITEMS: {
            setBulkItemBinder: gql`
            mutation SetBulkItemBinder($productIds: [String]!, $binder: ItemBinder!){
                setBulkItemBinder(productIds: $productIds, binder: $binder){
                    productId
                    claveProdServ
                    claveUnidad
                    customized
                    taxes{
                        ${ITEM_BINDER_TAXES_FULL_FIELDS}
                    }
                }
            }`,
            setItemBinder: gql`
            mutation SetItemBinder($binder: ItemBinder!){
                setItemBinder(binder: $binder){
                    productId
                    claveProdServ
                    claveUnidad
                    customized
                    taxes{
                        ${ITEM_BINDER_TAXES_FULL_FIELDS}
                    }
                    pedimento
                }
            }`,
            getItemsByName: gql`
                query GetItemsByName($name: String!, $page: Float){
                    getItemsByName(name: $name, page: $page){
                        page
                        pages
                        items {
                            item{
                                id
                                site_id
                                title
                                subtitle
                                seller_id
                                category
                                official_store_id
                                price
                                base_price
                                original_price
                                inventory_id
                                currency_id
                                initial_quantity
                                available_quantity
                                sold_quantity
                                secure_thumbnail
                                permalink
                                pictures {
                                    id
                                    url
                                    secure_url
                                    size
                                    max_size
                                    quality
                                }
                                shipping {
                                    mode
                                    local_pick_up
                                    free_shipping
                                    dimensions
                                    store_pick_up
                                }
                            }
                            binder{
                                claveProdServ
                                claveUnidad
                                customized
                                taxes {
                                    ${ITEM_BINDER_TAXES_FIELDS}
                                }
                                pedimento
                            }
                        }
                    }
                }
            `,
            getItems: gql`
                query GetItems{
                    getItems{
                        item{
                            _id
                            asin1
                            asin2
                            asin3
                            imageUrl
                            itemName
                            listingId
                            price
                            quantity
                            productId
                            sellerSku
                            status
                        }
                        binder{
                            productId
                            customized
                            claveProdServ
                            claveUnidad
                            taxes {
                                ${ITEM_BINDER_TAXES_FIELDS}
                            }
                            pedimento
                        }
                    }
                }
            `,
            getItemStatus: gql`
                query GetItemStatus{
                    getItemStatus{
                        fetchedAt
                        textStatus
                    }
                }
            `,
            getItemsByIds: gql`
                query GetItemsByIds($ids: [String]!){
                    getItemsByIds(ids: $ids) {
                        item{
                            id
                            site_id
                            title
                            subtitle
                            status
                            seller_id
                            category
                            official_store_id
                            price
                            base_price
                            original_price
                            inventory_id
                            currency_id
                            initial_quantity
                            available_quantity
                            sold_quantity
                            secure_thumbnail
                            permalink
                            pictures {
                                id
                                url
                                secure_url
                                size
                                max_size
                                quality
                            }
                            shipping {
                                mode
                                local_pick_up
                                free_shipping
                                dimensions
                                store_pick_up
                            }
                        }
                        binder{
                            claveProdServ
                            claveUnidad
                            customized
                            taxes {
                                ${ITEM_BINDER_TAXES_FIELDS}
                            }
                            pedimento
                        }
                    }
                }
            `,
            getCategorization: gql`
            query GetCategorization{
                getCategorization{
                    updatedAt
                    inProgress
                    dictionary{
                        category_id
                        category
                        count
                    }

                }
            }`,
            getItemBinders: gql`
            query GetItemBinders($itemIds: [String]!){
                getItemBinders(itemIds: $itemIds){
                    productId
                    customized
                    claveProdServ
                    claveUnidad
                    profileId
                    taxes {
                        ${ITEM_BINDER_TAXES_FULL_FIELDS}
                    }
                }
            }`
        },
        ORDERS: {
            getOrdersGlobal: gql`
            query GetOrdersGlobal($start: Float, $end: Float) {
                getOrdersGlobal(start: $start, end: $end) {
                    order {
                        ${ORDER_FIELDS}
                    }
                    binder{
                        ${ORDER_BINDER_FIELDS}
                    }
                    taxing {
                        subtotal
                        taxes {
                            type
                            tasa
                            exento
                        }
                    }
                }
            }`,
            getOrdersSearch: gql`
            query GetOrdersSearch($search: String) {
                getOrdersSearch(search: $search) {
                    order {
                        ${ORDER_FIELDS}
                    }
                    binder{
                        ${ORDER_BINDER_FIELDS}
                    }
                }
            }`,
            getOrders: gql`
            query GetOrders($start: Float, $end: Float, $page: Float) {
                getOrders(start: $start, end: $end, page: $page) {
                    pages
                    page
                    orders {
                        order {
                            ${ORDER_FIELDS}
                        }
                        binder{
                            ${ORDER_BINDER_FIELDS}
                        }
                    }
                }
            }`,
            getOrderById: gql`
            query GetOrderById($id: String) {
                getOrderById(id: $id) {
                    order {
                        order {
                            ${ORDER_FIELDS}
                            BuyerInfo {
                                BuyerEmail
                                BuyerName
                            }
                        }
                        binder{
                            ${ORDER_BINDER_FIELDS}
                        }
                    }
                    products {
                        item {
                            ASIN
                            OrderItemId
                            SellerSKU
                            Title
                            QuantityOrdered
                            QuantityShipped
                            ItemPrice {
                                Amount
                                CurrencyCode
                            }
                            ShippingPrice {
                                Amount
                                CurrencyCode
                            }
                        }
                        binder {
                            productId
                            customized
                            claveProdServ
                            claveUnidad
                            taxes {
                                ${ITEM_BINDER_TAXES_FIELDS}
                            }
                        }
                    }
                }
            }`,
            getOrderById_v2: gql`
            query GetOrderById_v2($id: String) {
                getOrderById_v2(id: $id) {
                    ${SEARCH_ORDER_WITH_PRODUCTS_FIELDS}
                }
            }`,
            getOrders_v2: gql`
            query GetOrders_v2($start: Float, $end: Float, $page: Float) {
                getOrders_v2(start: $start, end: $end, page: $page) {
                    ${SEARCH_ORDER_PAGINATED_FIELDS}
                }
            }`,
            getOrdersSearch_v2: gql`
            query GetOrdersSearch_v2($search: String) {
                getOrdersSearch_v2(search: $search) {
                    ${SEARCH_ORDER_BINDED_FIELDS}
                }
            }`,
            getOrdersGlobal_v2: gql`
            query GetOrdersGlobal_v2($start: Float, $end: Float) {
                getOrdersGlobal_v2(start: $start, end: $end) {
                    ${SEARCH_ORDER_GLOBAL_BINDED_FIELDS}
                }
            }`,
        },
        REPORTS: {
            reportBuildProgress: gql`
            subscription reportBuildProgress {
                reportBuildProgress {
                    _id
                    working
                    hasStarted
                    dataUrl
                    ordersFetchProgress {
                        hasNext
                        fetched
                    }
                    ordersBindedProgress{
                        hasNext
                        fetched
                    }
                    productsProgress{
                        hasNext
                        fetched
                    }
                    billsProgress{
                        hasNext
                        fetched
                    }
                }
              }
            `,
            buildReport: gql`
                mutation BuildReport($startDate: String!, $endDate: String!, $template: ReportTemplateInput!){
                    buildReport(startDate: $startDate, endDate: $endDate,template: $template)
                }
            `,
            getReportJob: gql`
                query GetReportJob($reportid: String){
                    getReportJob(reportId: $reportid){
                        ${REPORT_JOB_FIELDS}
                    }
                }`,
            getReportJobs: gql`
                query GetReportJobs{
                    getReportJobs{
                        ${REPORT_JOB_FIELDS}
                    }
                }`,
            saveReportTemplate: gql`
                mutation SaveReportTemplate($template: ReportTemplateInput!){
                    saveReportTemplate(template: $template){
                        ${REPORT_TEMPLATE_FIELDS}
                    }
                }
            `,
            getTemplateReports: gql`
                query GetTemplateReports{
                    getTemplateReports{
                        ${REPORT_TEMPLATE_FIELDS}
                    }
                }
            `
        }
    },
    FISCALPOP: {
        CFDI: {
            setRefundToStamp: gql`
                mutation SetRefundToStamp($cfdi: FiscalpopBill!, $refundIds: [String]!, $orderId: String!){
                    setRefundToStamp(cfdi: $cfdi, refundIds: $refundIds, orderId:$orderId){
                        ${ORDER_BINDER_FIELDS}
                    }
                }
            `,
            setBillToStamp: gql`
                mutation SetBillToStamp($cfdi: FiscalpopBill!, $orderId: String!, $refundIds: [String]){
                    setBillToStamp(cfdi: $cfdi, orderId: $orderId, refundIds: $refundIds){
                        ${ORDER_BINDER_FIELDS}
                    }
                }
            `,
            setGlobalBillToStamp: gql`
                mutation SetGlobalBillToStamp($cfdi: FiscalpopBill!, $orderIds: [String]!, $lastDate: Float){
                    setGlobalBillToStamp(cfdi: $cfdi, orderIds: $orderIds, lastDate: $lastDate){
                        UUID
                    }
                }
            `,
            setBillToCancel: gql`
            mutation SetBillToCancel($uuid: String!){
                setBillToCancel(uuid: $uuid){
                    folios {
                        estatus
                        uuid
                    }
                }
            }
            `,
            setOrderToCancel: gql`
            mutation SetOrderToCancel($orderId: String!){
                setOrderToCancel(orderId: $orderId){
                    canceled
                    refundUUID
                }
            }
            `,
        },
        REGIMEN: {
            getRegimenFiscal: gql`
                query GetRegimenFiscal{
                    getRegimenFiscal{
                        clave
                        descripcion
                    }
                }
            `
        },
        SETUP: {
            setFiscalpopClient: gql`
            mutation SetFiscalpopClient($client: FiscalpopClient!){
                setFiscalpopClient(client: $client){
                    rfc
                    regimenFiscal
                    nombre
                    lugarExpedicion
                }
            }`,
        },
        SUPPORT: {
            sendSupportEmail: gql`
            mutation SendSupportEmail($support: FiscalpopSupport!){
                sendSupportEmail(support: $support) {
                    success
                }
            }`
        },
        NOTIFICATONS: {
            getNewsWallConfirmed: gql`
            query GetNewsWallConfirmed{
                getNewsWallConfirmed {
                    ${NEWS_WALL_CONFIRMED_FIELDS}
                }
            }
            `,
            saveNewsWallConfirmed: gql`
            mutation SaveNewsWallConfirmed($newsId: String!){
                saveNewsWallConfirmed(newsId: $newsId) {
                    ${NEWS_WALL_CONFIRMED_FIELDS}
                }
            }`
        },
        PAYMENTS: {
            paymentMovements: gql`
            subscription PaymentMovements {
                paymentMovements {
                    orderId
                    billingId
                    amount
                    paid
                }
              }
            `,
            billingAccountUpdated: gql`
            subscription BillingAccountUpdated {
                billingAccountUpdated {
                    updatedAt
                    billingAccountId
                    paymentId
                    message
                }
            }`,
            billingAccountSubscriptionUpdated: gql`
            subscription BillingAccountSubscriptionUpdated {
                billingAccountSubscriptionUpdated {
                    updatedAt
                    billingAccountId
                    subscriptionId
                    subscriptionStatus
                    message
                }
            }`,
            speiPaymentRequested: gql`
            subscription SpeiPaymentRequested {
                speiPaymentRequested {
                    updatedAt
                    reference
                    amountCentsRemaining
                    billingAccountId
                    paymentIntentId
                    message
                }
            }`
        },
    },
    CONEKTA: {
        ORDERS: {
            getPaymentOrders: gql`
            query GetPaymentOrders {
                getPaymentOrders {
                    type
                    orderId
                    userId
                    amount
                    paid
                    currency
                    status
                    createdAt
                    expired
                    invoiceUUID
                }
            }`
        },
        SPEI: {
            requestSpeiPayment: gql`
            mutation RequestSpeiPayment($amount: Float!) {
                requestSpeiPayment(amount: $amount){
                    id
                    bank
                    clabe
                    amount
                    currency
                    order
                    quantity
                }
            }`
        },
        CARDS: {
            requestCardPayment: gql`
            mutation RequestCardPayment($amount: Float!, $sourceid: String!) {
                requestCardPayment(amount: $amount, sourceid: $sourceid) {
                    type
                    orderId
                    userId
                    amount
                    paid
                    currency
                    status
                    createdAt
                }
            }`,
            getCardPayments: gql`
                query GetCardPayments {
                    getCardPayments{
                        ${CONEKTA_CARD_FIELDS}
                    }
                }`,
            addCard: gql`
                mutation AddCard($card: ConektaCard!, $phone: String, $email: String){
                    addCard(card: $card, phone: $phone, email: $email){
                        ${CONEKTA_CARD_FIELDS}
                    }
                }`,
            removeCard: gql`
                mutation RemoveCard($paymentId: String!){
                    removeCard(paymentId: $paymentId){
                        ${CONEKTA_CARD_FIELDS}
                    }

                }`,
        }
    },
    STRIPE: {
        PAYMENTS: {
            createPaymentIntent: gql`
            mutation CreatePaymentIntent($amount: Float!){
                createPaymentIntent(amount: $amount) {
                    client_secret
                    identifier
                    status
                    amountCents
                }
            }`,
        },
        PRICES: {
            getPricingPlans: gql`
            query GetPricingPlans {
                getPricingPlans {
                    id
                    productId
                    name
                    maxUsage
                    isMeteredBilling
                    currency
                    unitAmountCents
                    cfdiMinAmountCents
                }
            }`,
            getCheckoutSession: gql`
            query GetCheckoutSession($sessionId: String!) {
                getCheckoutSession(sessionId: $sessionId) {
                    id
                    url
                    status
                    amountCents
                    subscription
                }
            }`,
            createCheckoutSession: gql`
            mutation CreateCheckoutSession($pricingId: String!) {
                createCheckoutSession(pricingId: $pricingId) {
                    id
                    url
                    status
                    amountCents
                    subscription
                }
            }`,
            changeSubscriptionPlan: gql`
            mutation ChangeSubscriptionPlan($pricingId: String!) {
                changeSubscriptionPlan(pricingId: $pricingId)
            }`,
            cancelSubscription: gql`
            mutation CancelSubscription {
                cancelSubscription
            }`,
            getUsageByRange: gql`
            query GetUsageByRange($start: Float!, $end: Float!) {
                getUsageByRange (start: $start, end: $end) {
                    aggregatedValue
                    startTime
                    endTime
                }
            }`
        },
        ORDERS: {
            getPaymentOrderRecords: gql`
            query GetPaymentOrderRecords {
                getPaymentOrderRecords {
                    type
                    gateway
                    orderId
                    amountCents
                    currency
                    isPaid
                    isRefunded
                    status
                    createdAt
                    updatedAt
                    invoiceUUID
                    refundInvoiceUUID
                }
            }`,
            getOpenSpeiPayment: gql`
            query GetOpenSpeiPayment {
                getOpenSpeiPayment {
                    paymentIntentId
                    amountCentsRemaining
                    instructionsUrl
                    reference
                }
            }`
        }
    }
}
